import {
  PAGE_SIZE,
  GEM_DEAL_THRESHOLD,
  DEALS_FILTER_VALUES,
  SORT_BY_CONFIG,
} from 'site-modules/shared/constants/lease-deals/lease-deals';
import { checkLength, maybe, wrapGteLte, wrapIn } from 'site-modules/shared/utils/incentives/gql-incentives-filter';

export class LeaseDealsFilterBuilder {
  #filters;
  #selectedFilters;

  constructor(selectedFilters) {
    this.#selectedFilters = selectedFilters;

    this.#filters = {};
  }

  withZip() {
    const { zipCode } = this.#selectedFilters;
    this.#filters.zip = zipCode;
    return this;
  }

  withIds() {
    const { id } = this.#selectedFilters;
    if (id) this.#filters.ids = id;
    return this;
  }

  withEffectivePaymentRatio() {
    const { deals } = this.#selectedFilters;
    this.#filters.effectivePaymentRatio = maybe(deals)
      .orElse([DEALS_FILTER_VALUES.ALL])
      .map(([dealType]) => (dealType === DEALS_FILTER_VALUES.GEMS ? [GEM_DEAL_THRESHOLD] : []))
      .map(checkLength)
      .map(wrapGteLte())
      .unpack();
    return this;
  }

  withEngineTypes() {
    const { engineType } = this.#selectedFilters;
    this.#filters.engineTypes = maybe(engineType).map(checkLength).map(wrapIn).unpack();
    return this;
  }

  withInventoryBodyTypes() {
    const { bodyType } = this.#selectedFilters;
    this.#filters.inventoryBodyTypes = maybe(bodyType).map(checkLength).map(wrapIn).unpack();
    return this;
  }

  withMakes() {
    const { make } = this.#selectedFilters;
    this.#filters.makes = maybe(make).map(checkLength).map(wrapIn).unpack();
    return this;
  }

  withModels() {
    const { model } = this.#selectedFilters;
    this.#filters.models = maybe(model).map(checkLength).map(wrapIn).unpack();
    return this;
  }

  withTerms() {
    const { term } = this.#selectedFilters;
    this.#filters.terms = maybe(term)
      .map(checkLength)
      .map(values => values.map(Number))
      .map(wrapIn)
      .unpack();
    return this;
  }

  withYearlyExcessMileages() {
    const { mileageAllowance } = this.#selectedFilters;
    this.#filters.yearlyExcessMileages = maybe(mileageAllowance)
      .map(checkLength)
      .map(values => values.map(Number))
      .map(wrapIn)
      .unpack();
    return this;
  }

  withNumberOfSeats() {
    const { totalSeating } = this.#selectedFilters;
    this.#filters.numberOfSeats = maybe(totalSeating)
      .map(checkLength)
      .map(values => values.map(Number))
      .map(wrapIn)
      .unpack();
    return this;
  }

  withPayment() {
    const { monthlyPayment } = this.#selectedFilters;
    this.#filters.payment = maybe(monthlyPayment).map(checkLength).map(wrapGteLte()).unpack();
    return this;
  }

  withDueAtSigning() {
    const { dueAtSigning } = this.#selectedFilters;
    this.#filters.dueAtSigning = maybe(dueAtSigning).map(checkLength).map(wrapGteLte()).unpack();
    return this;
  }

  withEdmundsTypeCategories() {
    const { edmundsTypeCategories } = this.#selectedFilters;
    this.#filters.edmundsTypeCategories = maybe(edmundsTypeCategories).map(checkLength).map(wrapIn).unpack();
    return this;
  }

  build() {
    return this.#filters;
  }
}

export class LeaseDealsQueryVariablesBuilder {
  #selectedFilters;
  #variables;

  constructor(selectedFilters) {
    this.#selectedFilters = selectedFilters;
    this.#variables = {};
  }

  withLeaseDealFilters(filters) {
    this.#variables.leaseDealFilters = filters;
    return this;
  }

  withPagination() {
    const { pageNum, id, pageSize } = this.#selectedFilters;

    if (id?.length) {
      this.#variables.page = { pageNum: 1, pageSize: 1 };
      return this;
    }

    this.#variables.page = { pageNum: pageNum?.[0] ?? 1, pageSize: pageSize ?? PAGE_SIZE };
    return this;
  }

  withSorts() {
    const { sortBy } = this.#selectedFilters;
    this.#variables.sorts = SORT_BY_CONFIG[sortBy[0]]?.sorts;
    return this;
  }

  build() {
    return this.#variables;
  }
}

const createLeaseDealsFilters = selectedFilters => {
  return new LeaseDealsFilterBuilder(selectedFilters)
    .withZip()
    .withEffectivePaymentRatio()
    .withEngineTypes()
    .withInventoryBodyTypes()
    .withMakes()
    .withModels()
    .withTerms()
    .withYearlyExcessMileages()
    .withNumberOfSeats()
    .withPayment()
    .withDueAtSigning()
    .withEdmundsTypeCategories()
    .build();
};

const createIdLeaseDealsFilter = selectedFilters => {
  return new LeaseDealsFilterBuilder(selectedFilters).withZip().withIds().build();
};

export const createLeaseDealsVariables = selectedFilters => {
  const leaseDealFilters = selectedFilters.id?.length
    ? createIdLeaseDealsFilter(selectedFilters)
    : createLeaseDealsFilters(selectedFilters);

  return new LeaseDealsQueryVariablesBuilder(selectedFilters)
    .withLeaseDealFilters(leaseDealFilters)
    .withPagination()
    .withSorts()
    .build();
};

export const createLeaseDealsFacetsVariables = selectedFilters => {
  const leaseDealFilters = createLeaseDealsFilters(selectedFilters);
  return new LeaseDealsQueryVariablesBuilder(selectedFilters).withLeaseDealFilters(leaseDealFilters).build();
};
